import * as React from 'react';
import cx from 'classnames';
import { map, trim, isEmpty, sortBy, join, split, filter, toLower } from 'lodash';
import type { DefaultOptionType } from 'antd/es/select';

import { Drawer, Form, Input, Button, Select, Checkbox, InputNumber } from 'antd';

import { IClient, useGetClientGroups, useGetClientFeatures } from '@frontend/app/hooks';

import { BRAND_CATEGORY_OPTIONS, BrandCategory } from '@frontend/app/config/constants';
import { UsageEntity } from '@services/users/types/UsageLimits';
import styles from './EditClientDrawer.scss';

const { Option } = Select;

const { useEffect, useMemo, useState } = React;

interface IFormValues {
  name: string;
  hostname?: string | null;
  salesForceId?: string | null;
  clientGroupId: number;
  domains: string;
  memberDomainsRestricted: boolean;
  featureIds: number[];
  disabledFeatureIds: number[];
  isTestClient: boolean;
  pfaOrderTag?: string | null;
  pfaLocationIds: string[];
  brandCategory: BrandCategory;
  maxMembersPerStandardProgram?: number | null;
  maxMembersPerFlexibleProgram?: number | null;
}

export interface ICreateClientParams {
  clientGroupId: number;
  name: string;
  hostname: string | null;
  domains: string[];
  featureIds: number[];
  disabledFeatureIds: number[];
  brandCategory: BrandCategory;
  pfaLocationIds: string[];
  usageLimits?: {
    [UsageEntity.MAX_MEMBERS_PER_STANDARD_PROGRAM]?: number | null;
    [UsageEntity.MAX_MEMBERS_PER_FLEXIBLE_PROGRAM]?: number | null;
  };
}
interface IProps {
  open: boolean;
  onRequstClose(): void;

  isSubmitting: boolean;
  onSubmit(client: ICreateClientParams): void;
  client?: IClient;

  className?: string;
}

interface IEditClientFormProps {
  onSubmit(client: ICreateClientParams): void;
  isSubmitting: boolean;

  client?: IClient;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};
const tailFormItemLayout = {
  wrapperCol: {
    xs: {
      span: 24,
      offset: 0,
    },
    sm: {
      span: 16,
      offset: 8,
    },
  },
};
const EditClientForm: React.FunctionComponent<React.PropsWithChildren<IEditClientFormProps>> = React.memo((props) => {
  const [form] = Form.useForm<IFormValues>();
  const { groups } = useGetClientGroups();
  const { features } = useGetClientFeatures();
  const [isTestClient, setIsTestClient] = useState(false);
  const [enableConversationPrivacy, setEnableConversationPrivacy] = useState(true);
  const defaultFeatures = ['messageTemplate', 'workflow', 'showOnboarding'];

  useEffect(() => {
    const { client } = props;
    if (!isEmpty(client) && client) {
      const clientAny = client as any;
      const usageLimits = clientAny?.usageLimits as
        | {
            [UsageEntity.MAX_MEMBERS_PER_STANDARD_PROGRAM]?: number | null;
            [UsageEntity.MAX_MEMBERS_PER_FLEXIBLE_PROGRAM]?: number | null;
          }
        | undefined;
      const values: IFormValues = {
        name: client.name ?? '',
        hostname: client.hostname ? toLower(trim(client.hostname)) : undefined,
        salesForceId: client.salesForceId ?? null,
        clientGroupId: client.clientGroupId ?? 0,
        domains: join(client.domains ?? [], ','),
        memberDomainsRestricted: client.memberDomainsRestricted ?? true,
        featureIds: client.featureIds ?? [],
        disabledFeatureIds: client.disabledFeatureIds ?? [],
        isTestClient: !!client.isTestClient,
        pfaOrderTag: client.pfaOrderTag ?? null,
        pfaLocationIds: client.pfaLocationIds ?? [],
        brandCategory: client.brandCategory as BrandCategory,
        maxMembersPerStandardProgram:
          usageLimits && typeof usageLimits === 'object' && UsageEntity.MAX_MEMBERS_PER_STANDARD_PROGRAM in usageLimits
            ? (usageLimits[UsageEntity.MAX_MEMBERS_PER_STANDARD_PROGRAM] ?? 1000)
            : 1000,
        maxMembersPerFlexibleProgram:
          usageLimits && typeof usageLimits === 'object' && UsageEntity.MAX_MEMBERS_PER_FLEXIBLE_PROGRAM in usageLimits
            ? (usageLimits[UsageEntity.MAX_MEMBERS_PER_FLEXIBLE_PROGRAM] ?? 10000)
            : 10000,
      };

      form.setFieldsValue(values);
      setIsTestClient(!!client.isTestClient);
      setEnableConversationPrivacy(!!client.memberDomainsRestricted);
    } else {
      const values: Partial<IFormValues> = {
        featureIds: map(
          filter(features, (feature) => defaultFeatures.includes(feature.name)),
          'id',
        ),
        memberDomainsRestricted: true,
        maxMembersPerStandardProgram: 1000,
        maxMembersPerFlexibleProgram: 10000,
      };
      form.setFieldsValue(values);
    }
  }, [form, props.client, features]);

  const SFDCRules = useMemo(() => {
    if (isTestClient) {
      return [];
    }

    return [{ required: true, message: `Please enter the client's SFDC ID.` }];
  }, [isTestClient]);

  const domainsRules = useMemo(() => {
    if (!enableConversationPrivacy) {
      return [];
    }

    return [{ required: true, message: 'Please, enter restricted domains' }];
  }, [enableConversationPrivacy]);
  const onFinish = (values: IFormValues) => {
    const {
      featureIds,
      disabledFeatureIds,
      domains,
      hostname,
      pfaLocationIds,
      maxMembersPerStandardProgram,
      maxMembersPerFlexibleProgram,
      ...rest
    } = values;

    props.onSubmit({
      ...rest,
      hostname: isEmpty(hostname) ? null : toLower(trim(hostname || '')),
      domains: isEmpty(domains) ? [] : map(split(domains, ','), trim),
      featureIds: map(featureIds, (featureId) => parseInt(String(featureId), 10)),
      disabledFeatureIds: map(disabledFeatureIds, (featureId) => parseInt(String(featureId), 10)),
      pfaLocationIds: map(pfaLocationIds ?? [], (id) => toLower(id)),
      usageLimits: {
        [UsageEntity.MAX_MEMBERS_PER_STANDARD_PROGRAM]: maxMembersPerStandardProgram ?? null,
        [UsageEntity.MAX_MEMBERS_PER_FLEXIBLE_PROGRAM]: maxMembersPerFlexibleProgram ?? null,
      },
    });
  };
  const onFinishFailed = (errorInfo: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (errorInfo.errorFields?.length > 0) {
      form.scrollToField(errorInfo.errorFields[0].name);
    }
  };
  const children: JSX.Element[] = [];
  for (let i = 10; i < 36; i++) {
    children.push(
      <Option key={i} value={i}>
        {i}
      </Option>,
    );
  }
  return (
    <Form form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} className={styles.form} {...formItemLayout}>
      <Form.Item
        label="Client Group"
        name="clientGroupId"
        rules={[{ required: true, message: 'Please select client group.' }]}
      >
        <Select showSearch placeholder="Select client group...">
          {map(
            sortBy(groups, (g) => g.id),
            (group) => (
              <Option key={group.id} value={group.id}>
                {group.id}
              </Option>
            ),
          )}
        </Select>
      </Form.Item>
      <Form.Item label="Name" name="name" rules={[{ required: true, message: `Please enter the client's name.` }]}>
        <Input placeholder="Client name" />
      </Form.Item>
      <Form.Item
        label="Category"
        name="brandCategory"
        rules={[{ required: true, message: 'Please select a category for the client.' }]}
      >
        <Select<BrandCategory>
          showSearch
          placeholder="Select client category..."
          optionFilterProp="children"
          filterOption={(input: string, option?: DefaultOptionType) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
          }
        >
          {BRAND_CATEGORY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value} label={option.label}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="SFDC ID" name="salesForceId" rules={SFDCRules}>
        <Input disabled={isTestClient} placeholder="Enter SFDC ID..." />
      </Form.Item>
      <Form.Item
        {...tailFormItemLayout}
        valuePropName="checked"
        name="isTestClient"
        extra="SFDC ID is not required for test clients."
      >
        <Checkbox onChange={(e) => setIsTestClient(e.target.checked)}>This is a test client</Checkbox>
      </Form.Item>
      <Form.Item
        label="Hostname"
        name="hostname"
        extra="Hostname has to be unique if the value is set, or you can leave it as empty."
      >
        <Input placeholder="Client hostname" />
      </Form.Item>
      <Form.Item label="Domains" name="domains" rules={domainsRules}>
        <Input placeholder="Enter client's domain..." />
      </Form.Item>
      <Form.Item
        {...tailFormItemLayout}
        valuePropName="checked"
        name="memberDomainsRestricted"
        extra="This will enable conversation privacy for client's domains"
      >
        <Checkbox onChange={(e) => setEnableConversationPrivacy(e.target.checked)}>Member domains restricted</Checkbox>
      </Form.Item>
      <Form.Item label="Client Features" name="featureIds">
        <Select mode="multiple" placeholder="Select client features..." optionFilterProp="name">
          {map(features, (feature) => (
            <Option key={feature.id} value={feature.id} name={feature.name}>
              {feature.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Disabled Client Features"
        name="disabledFeatureIds"
        extra="Here you can specify feature that will be disabled specifically for this client even if it is enabled for all."
      >
        <Select mode="multiple" placeholder="Select client features to disable..." optionFilterProp="name">
          {map(features, (feature) => (
            <Option key={feature.id} value={feature.id} name={feature.name}>
              {feature.name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="PFA Order Tag"
        name="pfaOrderTag"
        extra={
          <span>
            The orders will be tagged with <b>AspireIQ</b> if leave empty.
          </span>
        }
      >
        <Input placeholder="enter tag for Shopify order..." />
      </Form.Item>
      <Form.Item
        label="Online Inventory Locations:"
        name="pfaLocationIds"
        extra={
          <span>
            The <b>sellableOnlineQuantity</b> will be checked for OOS if left empty.
          </span>
        }
      >
        <Select mode="tags" placeholder="enter the locations ID for warehouse"></Select>
      </Form.Item>
      <Form.Item
        label="Members / Standard Program"
        name="maxMembersPerStandardProgram"
        extra="Maximum number of members allowed per standard program. Default: 1000, Range: 1000-10000"
        initialValue={1000}
        rules={[
          {
            type: 'number',
            min: 1000,
            max: 10000,
            message: 'Value must be between 1000 and 10000',
          },
        ]}
      >
        <InputNumber
          placeholder="Enter max members per standard program"
          min={1000}
          max={10000}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item
        label="Members / Flexible Program"
        name="maxMembersPerFlexibleProgram"
        extra="Maximum number of members allowed per flexible program. Default: 10000, Range: 1000-10000"
        initialValue={10000}
        rules={[
          {
            type: 'number',
            min: 1000,
            max: 10000,
            message: 'Value must be between 1000 and 10000',
          },
        ]}
      >
        <InputNumber
          placeholder="Enter max members per flexible program"
          min={1000}
          max={10000}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={props.isSubmitting} className={styles.submitButton}>
        Submit
      </Button>
    </Form>
  );
});

/**
 * @type {React.FunctionComponent}
 */
export const EditClientDrawer: React.FunctionComponent<React.PropsWithChildren<IProps>> = React.memo((props) => {
  return (
    <Drawer
      title={isEmpty(props.client) ? 'Create Client' : 'Edit Client'}
      visible={props.open}
      width={600}
      onClose={props.onRequstClose}
      className={cx(styles.EditClientDrawer, props.className)}
    >
      <EditClientForm
        client={props.client && !isEmpty(props.client) ? props.client : undefined}
        onSubmit={props.onSubmit}
        isSubmitting={props.isSubmitting}
      />
    </Drawer>
  );
});

EditClientDrawer.defaultProps = {
  className: undefined,
};
